import {
  ChangeEventHandler,
  ClipboardEventHandler,
  KeyboardEventHandler,
  useRef,
} from "react"

import { SingleCharacterInput } from "../../../base/input/SingleCharacterInput"

export interface OtpInputProps {
  // One string per digit cell; its length sets the number of cells.
  values: string[]
  onChange: (values: string[]) => void
  disabled?: boolean
  invalid?: boolean
  autoFocus?: boolean
  inputTestId?: string
}

// Segmented digit-only one-time-code field with inter-cell focus, backspace-to-previous, and paste-to-fill.
export function OtpInput({
  values,
  onChange,
  disabled,
  invalid,
  autoFocus = true,
  inputTestId,
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([])

  function onKeyDown(idx: number): KeyboardEventHandler<HTMLInputElement> {
    return (e) => {
      if (e.key === "Backspace" && !values[idx] && idx > 0) {
        refs.current[idx - 1]?.focus()
      }
    }
  }

  function onChangeCell(idx: number): ChangeEventHandler<HTMLInputElement> {
    return (e) => {
      const { value } = e.target
      if (value && !/^\d$/.test(value)) return

      const next = [...values]
      next[idx] = value
      onChange(next)

      if (!value) return
      if (idx < values.length - 1) {
        refs.current[idx + 1]?.focus()
      } else {
        e.target.blur()
      }
    }
  }

  function onPaste(idx: number): ClipboardEventHandler<HTMLInputElement> {
    return (e) => {
      if (idx !== 0) return
      e.preventDefault()
      const pasted = e.clipboardData.getData("text").trim()
      if (pasted.length !== values.length || !/^\d+$/.test(pasted)) return
      onChange(pasted.split(""))
      e.currentTarget.blur()
    }
  }

  return (
    <div className="flex justify-between">
      {values.map((value, idx) => (
        <SingleCharacterInput
          key={`otp-cell-${idx}`}
          data-testid={inputTestId ? `${inputTestId}-${idx}` : undefined}
          maxLength={1}
          size="sm"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={value}
          onKeyDown={onKeyDown(idx)}
          onChange={onChangeCell(idx)}
          onPaste={onPaste(idx)}
          ref={(el) => {
            refs.current[idx] = el
          }}
          autoFocus={autoFocus && idx === 0}
          disabled={disabled}
          invalid={invalid}
        />
      ))}
    </div>
  )
}
